export const content = {
  es: {
    siteTitle: 'Jesús Fernández Abeledo | Consultor de Accesibilidad Digital',
    skipLink: 'Saltar al contenido principal',
    nav: {
      about: 'Sobre mí',
      services: 'Servicios',
      experience: 'Experiencia',
      skills: 'Habilidades',
      contact: 'Contacto',
      menu: 'Menú',
      closeMenu: 'Cerrar menú',
      downloadCV: 'Descargar CV'
    },
    hero: {
      title: 'Consultor de Accesibilidad Web en Pontevedra y Galicia',
      subtitle: 'Jesús Fernández Abeledo | Auditor WCAG 2.2 y EN 301 549',
      description: 'Profesional con más de 5 años de experiencia en el ámbito digital, especializado en auditoría de accesibilidad web y app. Mi objetivo: crear experiencias digitales inclusivas para todos los usuarios.',
      cta1: 'Solicitar Auditoría',
      cta2: 'Descargar CV',
      location: 'Pontevedra · Galicia · España'
    },
    about: {
      title: 'Sobre mí',
      content: [
        'Profesional con más de 5 años de experiencia en el ámbito digital, especializado en auditoría de accesibilidad web y app.',
        'Mi formación en analítica digital me permite adoptar un enfoque técnico para identificar y resolver barreras de accesibilidad, asegurando que los productos digitales cumplan con los estándares WCAG.',
        'Empleo una metodología basada en datos para crear estrategias que promuevan la inclusión universal y la usabilidad. Mi objetivo: crear experiencias digitales inclusivas para todos los usuarios.'
      ]
    },
    services: {
      title: 'Servicios',
      list: [
        {
          title: 'Auditoría de Accesibilidad',
          description: 'Auditorías completas de sitios web y aplicaciones según normativas WCAG 2.2, EN 301 549 y RD 1112/2018.',
          icon: 'ClipboardCheck'
        },
        {
          title: 'Consultoría en Accesibilidad',
          description: 'Asesoramiento estratégico para integrar la accesibilidad en el ciclo de desarrollo de productos digitales.',
          icon: 'Users'
        },
        {
          title: 'Remediación Técnica',
          description: 'Implementación de soluciones técnicas para corregir barreras de accesibilidad identificadas.',
          icon: 'Wrench'
        },
        {
          title: 'Analítica Web',
          description: 'Implementación y optimización de herramientas de analítica (Google Analytics 4, GTM, GA360) para insights accionables.',
          icon: 'BarChart3'
        },
        {
          title: 'Formación y Capacitación',
          description: 'Talleres y formación en accesibilidad digital y mejores prácticas WCAG para equipos de desarrollo.',
          icon: 'GraduationCap'
        },
        {
          title: 'Pruebas con Tecnologías Asistivas',
          description: 'Evaluación con lectores de pantalla (NVDA, VoiceOver) y otras tecnologías asistivas.',
          icon: 'Monitor'
        }
      ]
    },
    experience: {
      title: 'Experiencia',
      jobs: [
        {
          role: 'Analista Consultor QA/QC - Accesibilidad Digital - TIC',
          company: 'Accesible Web',
          period: 'Enero 2020 - Presente',
          description: [
            'Auditoría y consultoría en accesibilidad digital para sitios web y aplicaciones',
            'Ejecución de proyectos end-to-end: auditoría, priorización y remediación técnica',
            'Trabajo con marcos normativos WCAG 2.2, EN 301 549 y RD 1112/2018',
            'Alineación de aspectos de negocio, legales y UX para reducir riesgos y mejorar conversión'
          ]
        },
        {
          role: 'Analista Digital',
          company: 'BBDO Proximity',
          period: 'Agosto 2024 - Diciembre 2024',
          description: [
            'Implementación de herramientas avanzadas de analítica: Google Analytics 360 y Google Tag Manager',
            'Gestión de xCampaign para análisis y gestión de campañas de marketing',
            'Desarrollo de informes interactivos en Looker Studio para análisis en tiempo real',
            'Gestión de herramientas de productividad: Office 365, Google Workspace, Trello, Teams'
          ]
        },
        {
          role: 'Analista Web - Analista en Marketing Digital',
          company: 'Freelancer',
          period: 'Febrero 2020 - Abril 2024',
          description: [
            'Implementación de herramientas de analítica (GA, GTM, Search Console, Microsoft Clarity)',
            'Creación y visualización de informes (Looker Studio, Tableau)',
            'Implementación de scripts, etiquetado de eventos y píxeles (Facebook Ads, Google Ads)',
            'Gestión de SEO local y comunidades en redes sociales'
          ]
        },
        {
          role: 'Consultor Manager Analítica Web / CRO',
          company: 'ILÚMINA SEO',
          period: 'Enero 2016 - Abril 2024',
          description: [
            'Auditoría, implementación y monitoreo de herramientas de analítica',
            'Implementación de Datalayer en diversas tecnologías (Android, iOS, WordPress)',
            'Configuración de herramientas de analítica digital para entornos web y app',
            'Gestión de SEO local'
          ]
        }
      ]
    },
    skills: {
      title: 'Habilidades',
      categories: [
        {
          title: 'Herramientas de Accesibilidad',
          items: ['Wave', 'Web Developer', 'Silktide', 'ANDI', 'Headings Map', 'NVDA', 'VoiceOver', 'axe DevTools', 'Colour Contrast Analyser', 'TalkBack', 'Google Lighthouse']
        },
        {
          title: 'Normativas y Estándares',
          items: ['WCAG 2.1', 'WCAG 2.2', 'EN 301 549', 'RD 1112/2018', 'WAI-ARIA', 'Section 508']
        },
        {
          title: 'Analítica Digital',
          items: ['Google Analytics 4', 'Google Analytics 360', 'Google Tag Manager', 'Google Search Console', 'Looker Studio', 'Adobe Analytics', 'Microsoft Clarity', 'Firebase']
        },
        {
          title: 'SEO y Marketing',
          items: ['Google Ads', 'Semrush', 'Sistrix', 'Screaming Frog', 'SEO Local', 'Google Business Profile']
        },
        {
          title: 'Productividad y Colaboración',
          items: ['Microsoft Office 365', 'Google Workspace', 'Trello', 'Teams', 'Planner', 'Jira']
        },
        {
          title: 'Técnicas',
          items: ['Implementación de Datalayer', 'Etiquetado de eventos', 'Píxeles de conversión', 'Estructuras de bases de datos', 'Remediación técnica HTML/CSS/JS']
        }
      ],
      certifications: {
        title: 'Certificaciones',
        list: [
          {
            title: 'Certificado Google Analytics 4',
            issuer: 'Google Skillshop',
            year: '2024'
          },
          {
            title: 'Certificado Profesional Análisis de Datos de Google',
            issuer: 'Coursera – Google',
            year: '2021'
          },
          {
            title: 'Certificado Profesional de Soporte de TI de Google',
            issuer: 'Coursera – Google',
            year: '2021'
          },
          {
            title: 'Técnico Superior en Producción de Audiovisuales, Radio y Espectáculos',
            issuer: 'Cinematography and Film/Video Production',
            year: '2002'
          }
        ]
      }
    },
    contact: {
      title: 'Contacto',
      description: 'Interesado en mejorar la accesibilidad de tu sitio web o app? Contáctame para una auditoría o consultoría.',
      form: {
        name: 'Nombre completo',
        nameRequired: 'Por favor, introduce tu nombre completo',
        email: 'Correo electrónico',
        emailRequired: 'Por favor, introduce tu correo electrónico',
        emailInvalid: 'Por favor, introduce un correo electrónico válido (ejemplo: nombre@dominio.com)',
        phone: 'Teléfono (opcional)',
        phoneInvalid: 'Por favor, introduce un número de teléfono válido (ejemplo: +34 600 000 000)',
        service: 'Servicio de interés',
        serviceRequired: 'Por favor, selecciona un servicio',
        servicePlaceholder: 'Selecciona un servicio',
        message: 'Mensaje',
        messageRequired: 'Por favor, escribe tu mensaje (mínimo 10 caracteres)',
        submit: 'Enviar mensaje',
        sending: 'Enviando...',
        success: 'Mensaje enviado correctamente. Te contactaré pronto.',
        error: 'Error al enviar el mensaje. Por favor, intenta de nuevo.'
      },
      info: {
        email: 's.fernandezabeledo@gmail.com',
        phone: '+34 680 746 254',
        location: 'Pontevedra, Galicia, España',
        linkedin: 'www.linkedin.com/in/jesusfernandezabeledo/'
      },
      cta: 'También puedes contactarme directamente por:'
    },
    legalPages: {
      backToHome: 'Volver al inicio',
      lastUpdated: 'Última actualización',
      legal: {
        title: 'Aviso legal',
        sections: [
          {
            title: 'Identificación',
            content:
              'Nombre: Jesús Fernández Abeledo\n\nDirección: Pontevedra, Galicia, España\n\nEmail: s.fernandezabeledo@gmail.com'
          },
          {
            title: 'Objeto',
            content:
              'El presente Aviso Legal regula el acceso y uso de este sitio web. El acceso implica la aceptación de las condiciones contenidas en el mismo.'
          },
          {
            title: 'Propiedad Intelectual',
            content:
              'Los contenidos del sitio (textos, imágenes, código) están protegidos por derechos de autor. Queda prohibida su reproducción sin autorización.'
          },
          {
            title: 'Responsabilidad',
            content:
              'El titular no se responsabiliza de los daños derivados del uso del sitio o de la información publicada que pueda ser modificada sin previo aviso.'
          },
          {
            title: 'Legislación Aplicable',
            content: 'Las condiciones se rigen por la legislación española.'
          }
        ]
      },
      privacy: {
        title: 'Política de privacidad',
        sections: [
          {
            title: 'Responsable del Tratamiento',
            content:
              'Responsable: Jesús Fernández Abeledo\n\nContacto: s.fernandezabeledo@gmail.com'
          },
          {
            title: 'Finalidad y Legitimación',
            content:
              'Los datos recogidos a través de formularios se utilizan para responder a consultas y gestionar comunicaciones. La base legal es el consentimiento del interesado.'
          },
          {
            title: 'Conservación',
            content: 'Los datos se conservarán el tiempo necesario para cumplir las finalidades o para atender obligaciones legales.'
          },
          {
            title: 'Derechos',
            content:
              'Puede ejercer los derechos de acceso, rectificación, supresión, oposición, portabilidad y limitación contactando en la dirección indicada.'
          }
        ]
      },
      accessibility: {
        title: 'Declaración de accesibilidad',
        sections: [
          {
            title: 'Estado de conformidad',
            content:
              'Este sitio tiene por objetivo cumplir con WCAG 2.2 Nivel AA. Se han realizado comprobaciones automatizadas y pruebas manuales con tecnologías asistivas.'
          },
          {
            title: 'Evaluación',
            content:
              'Se han utilizado herramientas automáticas y pruebas con lectores de pantalla (NVDA, VoiceOver) para detectar barreras.'
          },
          {
            title: 'Contenido no accesible',
            content: 'Si detecta barreras de accesibilidad, por favor indíquelas con la URL y una breve descripción.'
          },
          {
            title: 'Contacto',
            content: 'Email: s.fernandezabeledo@gmail.com'
          }
        ]
      }
    },
    footer: {
      copyright: '© 2025 Jesús Fernández Abeledo. Todos los derechos reservados.',
      wcag: 'Este sitio cumple con WCAG 2.2 Nivel AA',
      privacy: 'Política de privacidad',
      legal: 'Aviso legal',
      accessibility: 'Declaración de accesibilidad',
      navigation: 'Navegación'
    }
  },
  en: {
    siteTitle: 'Jesús Fernández Abeledo | Digital Accessibility Consultant',
    skipLink: 'Skip to main content',
    nav: {
      about: 'About',
      services: 'Services',
      experience: 'Experience',
      skills: 'Skills',
      contact: 'Contact',
      menu: 'Menu',
      closeMenu: 'Close menu',
      downloadCV: 'Download CV'
    },
    hero: {
      title: 'Digital Accessibility Consultant in Spain',
      subtitle: 'Jesús Fernández Abeledo | Auditor WCAG 2.2 & EN 301 549',
      description: 'Professional with over 5 years of experience in the digital field, specialized in web and app accessibility auditing. My goal: to create inclusive digital experiences for all users.',
      cta1: 'Request Audit',
      cta2: 'Download CV',
      location: 'Pontevedra · Galicia · Spain'
    },
    about: {
      title: 'About Me',
      content: [
        'Professional with over 5 years of experience in the digital field, specialized in web and app accessibility auditing.',
        'My background in digital analytics allows me to adopt a technical approach to identify and resolve accessibility barriers, ensuring digital products comply with WCAG standards.',
        'I employ a data-driven methodology to create strategies that promote universal inclusion and usability. My goal: to create inclusive digital experiences for all users.'
      ]
    },
    services: {
      title: 'Services',
      list: [
        {
          title: 'Accessibility Auditing',
          description: 'Complete audits of websites and applications according to WCAG 2.2, EN 301 549 and RD 1112/2018 regulations.',
          icon: 'ClipboardCheck'
        },
        {
          title: 'Accessibility Consulting',
          description: 'Strategic consulting to integrate accessibility into the digital product development cycle.',
          icon: 'Users'
        },
        {
          title: 'Technical Remediation',
          description: 'Implementation of technical solutions to correct identified accessibility barriers.',
          icon: 'Wrench'
        },
        {
          title: 'Web Analytics',
          description: 'Implementation and optimization of analytics tools (Google Analytics 4, GTM, GA360) for actionable insights.',
          icon: 'BarChart3'
        },
        {
          title: 'Training & Education',
          description: 'Workshops and training on digital accessibility and WCAG best practices for development teams.',
          icon: 'GraduationCap'
        },
        {
          title: 'Assistive Technology Testing',
          description: 'Evaluation with screen readers (NVDA, VoiceOver) and other assistive technologies.',
          icon: 'Monitor'
        }
      ]
    },
    experience: {
      title: 'Experience',
      jobs: [
        {
          role: 'QA/QC Analyst Consultant - Digital Accessibility - ICT',
          company: 'Accesible Web',
          period: 'January 2020 - Present',
          description: [
            'Digital accessibility auditing and consulting for websites and applications',
            'End-to-end project execution: auditing, prioritization and technical remediation',
            'Work with regulatory frameworks WCAG 2.2, EN 301 549 and RD 1112/2018',
            'Alignment of business, legal and UX aspects to reduce risks and improve conversion'
          ]
        },
        {
          role: 'Digital Analyst',
          company: 'BBDO Proximity',
          period: 'August 2024 - December 2024',
          description: [
            'Implementation of advanced analytics tools: Google Analytics 360 and Google Tag Manager',
            'xCampaign management for marketing campaign analysis and management',
            'Development of interactive reports in Looker Studio for real-time analysis',
            'Productivity tools management: Office 365, Google Workspace, Trello, Teams'
          ]
        },
        {
          role: 'Web Analyst - Digital Marketing Analyst',
          company: 'Freelancer',
          period: 'February 2020 - April 2024',
          description: [
            'Implementation of analytics tools (GA, GTM, Search Console, Microsoft Clarity)',
            'Report creation and visualization (Looker Studio, Tableau)',
            'Script implementation, event tagging and pixels (Facebook Ads, Google Ads)',
            'Local SEO management and social media community management'
          ]
        },
        {
          role: 'Web Analytics / CRO Manager Consultant',
          company: 'ILÚMINA SEO',
          period: 'January 2016 - April 2024',
          description: [
            'Auditing, implementation and monitoring of analytics tools',
            'Datalayer implementation in various technologies (Android, iOS, WordPress)',
            'Configuration of digital analytics tools for web and app environments',
            'Local SEO management'
          ]
        }
      ]
    },
    skills: {
      title: 'Skills',
      categories: [
        {
          title: 'Accessibility Tools',
          items: ['Wave', 'Web Developer', 'Silktide', 'ANDI', 'Headings Map', 'NVDA', 'VoiceOver', 'axe DevTools', 'Colour Contrast Analyser', 'TalkBack', 'Google Lighthouse']
        },
        {
          title: 'Standards & Regulations',
          items: ['WCAG 2.1', 'WCAG 2.2', 'EN 301 549', 'RD 1112/2018', 'WAI-ARIA', 'Section 508']
        },
        {
          title: 'Digital Analytics',
          items: ['Google Analytics 4', 'Google Analytics 360', 'Google Tag Manager', 'Google Search Console', 'Looker Studio', 'Adobe Analytics', 'Microsoft Clarity', 'Firebase']
        },
        {
          title: 'SEO & Marketing',
          items: ['Google Ads', 'Semrush', 'Sistrix', 'Screaming Frog', 'Local SEO', 'Google Business Profile']
        },
        {
          title: 'Productivity & Collaboration',
          items: ['Microsoft Office 365', 'Google Workspace', 'Trello', 'Teams', 'Planner', 'Jira']
        },
        {
          title: 'Technical Skills',
          items: ['Datalayer Implementation', 'Event Tagging', 'Conversion Pixels', 'Database Structures', 'Technical Remediation HTML/CSS/JS']
        }
      ],
      certifications: {
        title: 'Certifications',
        list: [
          {
            title: 'Google Analytics 4 Certificate',
            issuer: 'Google Skillshop',
            year: '2024'
          },
          {
            title: 'Google Data Analytics Professional Certificate',
            issuer: 'Coursera – Google',
            year: '2021'
          },
          {
            title: 'Google IT Support Professional Certificate',
            issuer: 'Coursera – Google',
            year: '2021'
          },
          {
            title: 'Higher Technician in Audiovisual Production, Radio and Shows',
            issuer: 'Cinematography and Film/Video Production',
            year: '2002'
          }
        ]
      }
    },
    contact: {
      title: 'Contact',
      description: 'Interested in improving the accessibility of your website or app? Contact me for an audit or consultation.',
      form: {
        name: 'Full name',
        nameRequired: 'Please enter your full name',
        email: 'Email address',
        emailRequired: 'Please enter your email address',
        emailInvalid: 'Please enter a valid email address (example: name@domain.com)',
        phone: 'Phone (optional)',
        phoneInvalid: 'Please enter a valid phone number (example: +34 600 000 000)',
        service: 'Service of interest',
        serviceRequired: 'Please select a service',
        servicePlaceholder: 'Select a service',
        message: 'Message',
        messageRequired: 'Please write your message (minimum 10 characters)',
        submit: 'Send message',
        sending: 'Sending...',
        success: 'Message sent successfully. I will contact you soon.',
        error: 'Error sending message. Please try again.'
      },
      info: {
        email: 's.fernandezabeledo@gmail.com',
        phone: '+34 680 746 254',
        location: 'Pontevedra, Galicia, Spain',
        linkedin: 'www.linkedin.com/in/jesusfernandezabeledo/'
      },
      cta: 'You can also contact me directly via:'
    },
    legalPages: {
      backToHome: 'Back to home',
      lastUpdated: 'Last updated',
      legal: {
        title: 'Legal Notice',
        sections: [
          {
            title: 'Identification',
            content:
              'Name: Jesús Fernández Abeledo\n\nLocation: Pontevedra, Galicia, Spain\n\nEmail: s.fernandezabeledo@gmail.com'
          },
          {
            title: 'Purpose',
            content:
              'This Legal Notice governs access to and use of this website. Access implies acceptance of the conditions contained herein.'
          },
          {
            title: 'Intellectual Property',
            content:
              'All website contents (texts, images, code) are protected by copyright. Reproduction without authorization is prohibited.'
          },
          {
            title: 'Liability',
            content:
              'The owner is not responsible for damages resulting from the use of the website or information that may change without notice.'
          },
          {
            title: 'Applicable Law',
            content: 'These terms are governed by the laws of Spain.'
          }
        ]
      },
      privacy: {
        title: 'Privacy Policy',
        sections: [
          {
            title: 'Data Controller',
            content:
              'Controller: Jesús Fernández Abeledo\n\nContact: s.fernandezabeledo@gmail.com'
          },
          {
            title: 'Purpose and Legal Basis',
            content:
              'Personal data collected through forms is used to respond to inquiries and manage communications. The legal basis is the consent of the user.'
          },
          {
            title: 'Retention',
            content: 'Data will be kept only as long as necessary to fulfill purposes or comply with legal obligations.'
          },
          {
            title: 'Rights',
            content:
              'You can exercise rights of access, rectification, erasure, objection, portability and restriction by contacting the email provided.'
          }
        ]
      },
      accessibility: {
        title: 'Accessibility Statement',
        sections: [
          {
            title: 'Conformance Status',
            content:
              'This website aims to conform to WCAG 2.2 Level AA. Automated checks and manual tests with assistive technologies have been carried out.'
          },
          {
            title: 'Evaluation',
            content:
              'The site has been evaluated using automated tools and manual testing with screen readers such as NVDA and VoiceOver.'
          },
          {
            title: 'Non-accessible Content',
            content: 'If you find accessibility barriers, please report them including the URL and a short description.'
          },
          {
            title: 'Contact',
            content: 'Email: s.fernandezabeledo@gmail.com'
          }
        ]
      }
    },
    footer: {
      copyright: '© 2025 Jesús Fernández Abeledo. All rights reserved.',
      wcag: 'This site complies with WCAG 2.2 Level AA',
      privacy: 'Privacy Policy',
      legal: 'Legal Notice',
      accessibility: 'Accessibility statement',
      navigation: 'Navigation'
    }
  }
};

export const serviceOptions = {
  es: [
    { value: 'auditoria', label: 'Auditoría de Accesibilidad' },
    { value: 'consultoria', label: 'Consultoría en Accesibilidad' },
    { value: 'remediacion', label: 'Remediación Técnica' },
    { value: 'analitica', label: 'Analítica Web' },
    { value: 'formacion', label: 'Formación y Capacitación' },
    { value: 'otro', label: 'Otro' }
  ],
  en: [
    { value: 'audit', label: 'Accessibility Audit' },
    { value: 'consulting', label: 'Accessibility Consulting' },
    { value: 'remediation', label: 'Technical Remediation' },
    { value: 'analytics', label: 'Web Analytics' },
    { value: 'training', label: 'Training & Education' },
    { value: 'other', label: 'Other' }
  ]
};
